/**
 * @file CardTiles.tsx
 * @brief Saved cards as small tiles (Billing redesign, DES 21-10-2026), for
 *        the company admin's Billing and the personal Billing → Cards.
 *
 * @details
 * Each tile: the card network's mark, •••• last4, expiry, a "Default" tag
 * (the default tile has a brand ring), the page's own ⋯ menu, and an
 * optional line under it (e.g. "Checking with your bank…"). The last tile is
 * a dashed "Add card".
 *
 * @author DES
 * @date 21-10-2026
 */

import type { ReactNode } from "react";
import { Plus } from "lucide-react";
import { CardBrandIcon } from "@/features/company-billing/components/CardBrandIcon";
import { cn } from "@/shared/theme/cn";

export type CardTile = {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
};

export function CardTiles({
  cards,
  menu,
  below,
  onAdd,
  addDisabled,
}: {
  cards: readonly CardTile[];
  /** The page's ⋯ menu for one card. */
  menu: (card: CardTile) => ReactNode;
  /** A line under one card's tile (status), or nothing. */
  below?: (card: CardTile) => ReactNode;
  onAdd?: () => void;
  addDisabled?: boolean;
}) {
  return (
    <div className="grid grid-cols-1 gap-2.5 px-4 py-4 sm:grid-cols-2 md:px-5 lg:grid-cols-3 xl:grid-cols-5">
      {cards.map((card) => (
        <div
          key={card.id}
          className={cn(
            "flex min-h-[108px] min-w-0 flex-col justify-between rounded-2xl border bg-app-card p-3.5 transition-colors",
            card.isDefault ? "border-brand ring-1 ring-inset ring-brand" : "border-border/70",
          )}
        >
          <div className="flex items-center justify-between gap-2">
            <CardBrandIcon brand={card.brand} className="h-6 w-10" />
            <div className="flex items-center gap-1">
              {card.isDefault ? (
                <span className="rounded-full bg-brand/[0.14] px-2 py-0.5 text-[11px] font-semibold text-brand">
                  Default
                </span>
              ) : null}
              {menu(card)}
            </div>
          </div>
          <div className="mt-3 min-w-0">
            <p className="font-medium tracking-[0.14em] text-foreground tabular-nums">•••• {card.last4}</p>
            <p className="text-xs text-muted-foreground">
              Expires {String(card.expMonth).padStart(2, "0")}/{String(card.expYear).slice(-2)}
            </p>
            {below ? below(card) : null}
          </div>
        </div>
      ))}
      {onAdd ? (
        <button
          type="button"
          onClick={onAdd}
          disabled={addDisabled}
          className="flex min-h-[108px] flex-col items-center justify-center gap-1.5 rounded-2xl border border-dashed border-border text-sm text-muted-foreground transition-colors hover:border-brand/60 hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
        >
          <Plus className="h-5 w-5" />
          Add card
        </button>
      ) : null}
    </div>
  );
}
